/*
  This is a MOCK of the ssr.js file, to simulate the calling of component getSSRAction() methods.
  The only difference is we are doing this on the client, not on the server. The code is identical.
 */

import { stringify } from 'flatted/cjs'; // Deals with circular references in objects

/**
 * Iterate over an array of components and build a map of actions to call.
 * The action-map will only contain UNIQUE action+param requests.
 * This means there is no harm in reading an entire component tree looking for
 * components with getSSRActions() even if there are components which need to call the same
 * action, as the action will only be called once.
 *
 * @param {Object} context
 * @param {Array} components
 * @return {any[]}        An array of unique Store actions to call
 */
export function getComponentActions(context, components = []) {
  const uniqueActions = new Map();

  components.forEach(component => {
    if (typeof component.getSSRActions === 'function') {
      const actionList = component.getSSRActions(context, getComponentActions);
      actionList.forEach(action => uniqueActions.set(stringify(action), action));
    }
  });

  return Array.from(uniqueActions.values()); // Convert from an Iterator into an Array
}

/**
 * Calls store.dispatch() on the array of component actions
 * We can optionally deal with one level of descendents (children)
 * @param {Function} dispatchFn
 * @param {Array} actions
 * @return Array<Promise>
 */
export function dispatchComponentActions(dispatchFn, actions) {
  return actions.map(action => dispatchFn(action.name, action.params));
}

/**
 * Convenience method which uses Promise.all() to wait for all the component SSR actions to resolve
 * (by wrapping all the promises in a new promise)
 * @param context
 * @param components
 * @returns {Promise<T[]>}
 */
export function resolveAllComponentActions(context, components) {
  return Promise.all(
    dispatchComponentActions(context.store.dispatch, getComponentActions(context, Object.values(components)))
  );
}
